import type {
  AgendaSlotLinkStatus,
  AgendaSlotStatus,
  AgendaSlotType,
} from "~/ui-rtk/api/agendaApi"
import { capitalize } from "~/ui-rtk/utils/format"

export const SLOT_TYPE_META: Record<
  AgendaSlotType,
  { label: string; badgeClass: string; actionLabel: string }
> = {
  AD_BLAST: {
    label: "Ad Blast",
    badgeClass: "badge-primary",
    actionLabel: "Open Ad Blaster",
  },
  AD_CREATIVE: {
    label: "Ad Creative",
    badgeClass: "badge-secondary",
    actionLabel: "Open Ad Forge",
  },
  BUDGET_RECOMMENDATION: {
    label: "Budget",
    badgeClass: "badge-accent",
    actionLabel: "Open Budget Recommender",
  },
  GENERIC: {
    label: "Task",
    badgeClass: "badge-ghost",
    actionLabel: "Open link",
  },
}

export const SLOT_STATUS_META: Record<
  AgendaSlotStatus,
  { label: string; badgeClass: string }
> = {
  TODO: { label: "To do", badgeClass: "badge-ghost" },
  IN_PROGRESS: { label: "In progress", badgeClass: "badge-info" },
  IN_REVIEW: { label: "In review", badgeClass: "badge-accent" },
  BLOCKED: { label: "Blocked", badgeClass: "badge-warning" },
  DONE: { label: "Done", badgeClass: "badge-success" },
  CANCELLED: { label: "Cancelled", badgeClass: "badge-error" },
}

export const LINK_STATUS_META: Record<
  AgendaSlotLinkStatus,
  { label: string; badgeClass: string }
> = {
  PENDING: { label: "Running", badgeClass: "badge-info" },
  DELIVERED: { label: "Delivered", badgeClass: "badge-accent" },
  APPROVED: { label: "Approved", badgeClass: "badge-success" },
  REJECTED: { label: "Changes requested", badgeClass: "badge-warning" },
}

export const SLOT_TYPE_OPTIONS = (
  Object.keys(SLOT_TYPE_META) as AgendaSlotType[]
).map(value => ({ value, label: SLOT_TYPE_META[value].label }))

export const SLOT_STATUS_OPTIONS = (
  Object.keys(SLOT_STATUS_META) as AgendaSlotStatus[]
).map(value => ({ value, label: SLOT_STATUS_META[value].label }))

export type AuditTypeMeta = {
  label: string
  /** Colored left accent + soft (~15%) tint, matching the slot chips above. */
  accentClass: string
  /** Solid swatch of the same color, for dots/legends outside the calendar. */
  dotClass: string
}

/**
 * Known audit type slugs (the recorded-action history overlaid on the
 * calendar). The audit `type` column is an open set (any kebab-case slug), so
 * unknown types fall back to a generated label + gray.
 */
export const AUDIT_TYPE_META: Record<string, AuditTypeMeta> = {
  "budget-recommendation": {
    label: "Budget Recommendation",
    accentClass: "!border-l-accent !bg-accent/15",
    dotClass: "bg-accent",
  },
  "budget-update": {
    label: "Budget Update",
    accentClass: "!border-l-sky-400 !bg-sky-500/15",
    dotClass: "bg-sky-400",
  },
  "status-update": {
    label: "Status Update",
    accentClass: "!border-l-emerald-400 !bg-emerald-500/15",
    dotClass: "bg-emerald-400",
  },
  "ad-blast": {
    label: "Ad Blast",
    accentClass: "!border-l-primary !bg-primary/15",
    dotClass: "bg-primary",
  },
  "ad-creative": {
    label: "Ad Creative",
    accentClass: "!border-l-secondary !bg-secondary/15",
    dotClass: "bg-secondary",
  },
  "ad-forge-image-gen": {
    label: "Image Generation",
    accentClass: "!border-l-orange-400 !bg-orange-500/15",
    dotClass: "bg-orange-400",
  },
}

export function auditTypeMeta(type: string): AuditTypeMeta {
  return (
    AUDIT_TYPE_META[type] ?? {
      label: type.split("-").map(capitalize).join(" "),
      accentClass: "!border-l-system-gray-500 !bg-system-gray-500/15",
      dotClass: "bg-system-gray-500",
    }
  )
}
